// typescript
import React from "react";
import {Localizer} from "@/main/react/Types";
import {MetaHandler} from "@/main/common/MetaHandler";

export type DateFormatOptions = {
    showDay?: boolean;
    showMonth?: boolean;
    showYear?: boolean;
    fallback?: string;
};

export type DateFormatterDisplayProps = {
    localizer: Localizer;
    date?: Date | string | number | null;
    relative?: boolean;
    fallback?: string;
    showDay?: boolean;
    showMonth?: boolean;
    showYear?: boolean;
    className?: string;
};

export default class DateFormatter {
    private localizer: Localizer;
    private localeCode?: string;

    constructor(localizer: Localizer) {
        this.localizer = localizer;
        this.localeCode = typeof MetaHandler.getCurrentLanguageTag === "function"
            ? MetaHandler.getCurrentLanguageTag()
            : String(MetaHandler.getCurrentLocale());
    }

    format(dateInput: Date | string | number, opts: DateFormatOptions = {}): string {
        const {showDay = true, showMonth = true, showYear = true, fallback = "-"} = opts;
        const date = (dateInput instanceof Date) ? dateInput : new Date(dateInput as any);
        if (isNaN(date.getTime())) return fallback;

        if (!showDay && !showMonth && !showYear) {
            return fallback;
        }

        return new Intl.DateTimeFormat(this.localeCode || "en", {
            day: showDay ? "numeric" : undefined,
            month: showMonth ? "long" : undefined,
            year: showYear ? "numeric" : undefined,
        }).format(date);
    }

    // Long localized date plus time (e.g. "May 31, 2026 3:04 PM"), respecting the active UI locale.
    formatDateTime(dateInput: Date | string | number, opts: DateFormatOptions = {}): string {
        const {fallback = "-"} = opts;
        const date = (dateInput instanceof Date) ? dateInput : new Date(dateInput as any);
        if (isNaN(date.getTime())) return fallback;

        const datePart = this.format(date, opts);
        const timePart = new Intl.DateTimeFormat(this.localeCode || "en", {
            hour: "numeric",
            minute: "2-digit",
        }).format(date);
        return `${datePart} ${timePart}`;
    }

    // prefer server-provided "now" for consistent comparisons; fallback to client now
    private getNow(): Date {
        const serverDate = MetaHandler.getServerDate?.();
        return serverDate ?? new Date();
    }

    timeSince(dateInput: Date | string | number): string {
        const date = (dateInput instanceof Date) ? dateInput : new Date(dateInput);
        const now = this.getNow(); // server time preferred; client fallback
        let seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
        if (seconds < 0) seconds = 0;

        if (seconds < 59) {
            return this.localizer("time.now");
        }

        // Each key is an ICU plural value, so the count alone picks the form for the locale.
        let interval = Math.floor(seconds / 31536000);
        if (interval >= 1) return this.localizer("time.year.ago", interval);

        interval = Math.floor(seconds / 2592000);
        if (interval >= 1) return this.localizer("time.month.ago", interval);

        interval = Math.floor(seconds / 86400);
        if (interval >= 1) return this.localizer("time.day.ago", interval);

        interval = Math.floor(seconds / 3600);
        if (interval >= 1) return this.localizer("time.hour.ago", interval);

        interval = Math.floor(seconds / 60);
        if (interval >= 1) return this.localizer("time.minute.ago", interval);

        return this.localizer("time.second.ago", seconds);
    }

}

export function DateFormatterDisplay({
    localizer,
    date,
    relative = false,
    fallback = "-",
    showDay,
    showMonth,
    showYear,
    className,
}: DateFormatterDisplayProps) {
    const formatter = React.useMemo(() => new DateFormatter(localizer), [localizer]);
    const value = React.useMemo(() => {
        if (date == null || date === "") {
            return fallback;
        }

        return relative
            ? formatter.timeSince(date)
            : formatter.format(date, {showDay, showMonth, showYear, fallback});
    }, [date, fallback, formatter, relative, showDay, showMonth, showYear]);

    return <span className={className}>{value}</span>;
}
